import { useEffect, useMemo, useState } from "react";

import { CheckSquare2, Loader2, Settings2 } from "lucide-react";

import { useTableSort } from "@/components/helpers/custom-hooks/use-table-sort";
import { LedgerAccountKeyUtils } from "@/components/helpers/ledger/ledger";
import { pluralize } from "@/components/helpers/string-utils";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { AddNewUsersCallout } from "@/components/pages_logged_in/workspace-admin/AddNewUsersCallout";
import { BulkInviteScheduleDetailDialog } from "@/components/pages_logged_in/workspace-admin/BulkInviteScheduleDetailDialog";
import { BulkInviteSchedulingBanner } from "@/components/pages_logged_in/workspace-admin/BulkInviteSchedulingBanner";
import { MemberDetailsDialog } from "@/components/pages_logged_in/workspace-admin/MemberDetailsDialog";
import { getWorkspaceAdminTabs } from "@/components/pages_logged_in/workspace-admin/WorkspaceAdminTabs";
import { WorkspaceCredits } from "@/components/pieces/coaching/workspace-credits/WorkspaceCredits";
import { getUser } from "@/components/session/JwtTokenManager";
import { Callout } from "@/components/ui/callout";
import { Card, CardContent } from "@/components/ui/card";
import { Filter } from "@/components/ui/filter";
import { SearchInput } from "@/components/ui/search-input";
import {
  JobStatus,
  JobType,
  useWorkspaceClientStatisticsQuery,
  useWorkspaceCreditsQuery,
  useWorkspaceGroupsQuery,
  useWorkspaceInvitaionBatchesStatusQuery,
  useWorkspaceScheduleJobsQuery,
  useWorkspaceSubscriptionQuery,
  WorkspaceGroupsQuery,
  WorkspaceInvitationBatchesStatus,
  WorkspaceMemberRole,
} from "@/graphql";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { cn } from "@/lib/utils";
import {
  MemberTableField,
  OnCheckHandler,
  StatsMember,
  WorkspaceScheduleJob,
} from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";

import { AddUsersDialog } from "./AddUsersDialog";
import { ModifyCreditsDialog } from "./ModifyCreditsDialog";
import { Paging } from "./Paging";
import { RemoveUsersDialog } from "./RemoveUsersDialog";
import { SetGroupForUsersDialog } from "./SetGroupForUsersDialog";
import { TransferCreditsDialog } from "./TransferCreditsDialog";
import { UpdateUserRoleDialog } from "./UpdateUserRoleDialog";
import { WorkspaceAdminController } from "./WorkspaceAdminController";
import { WorkspaceAdminHeader } from "./WorkspaceAdminHeader";
import { WorkspaceMemberTable } from "./WorkspaceMemberTable";
import { WorkspaceSeats } from "./WorkspaceSeats";

const FETCH_LIMIT = 10;
const POLLING_INTERVAL_IN_MILLISECONDS = 1500;

type Dialogs =
  | "modify-credits"
  | "transfer-credits"
  | "add-users"
  | "update-user-role"
  | "remove-users"
  | "set-group";

type WorkspaceGroup = NonNullable<
  WorkspaceGroupsQuery["workspaceGroups"]
>[number];

interface RoleDto {
  userName: string;
  memberId: string;
  currentRole: WorkspaceMemberRole;
  newRole: WorkspaceMemberRole;
  shouldReload: boolean;
}

function WorkspaceAdminContainer() {
  const currentUser = getUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const [checkedData, setCheckedData] = useState<string[]>([]);
  const [selectAllUsers, setSelectAllUsers] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState<string[]>([]);
  const [needAllUsersData, setNeedAllUsersData] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [workspaceGroups, setWorkspaceGroups] = useState<WorkspaceGroup[]>([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting
  const { currentSort, updateSortState } = useTableSort<MemberTableField>(
    "workspaceClientStatisticSort",
  );

  // Filtering
  const { data: workspaceGroupsData, loading: areGroupsLoading } =
    useWorkspaceGroupsQuery();

  const groupIds =
    selectedGroups.length !== workspaceGroups.length ? selectedGroups : null;

  useEffect(() => {
    if (workspaceGroupsData?.workspaceGroups) {
      setSelectedGroups(workspaceGroupsData.workspaceGroups.map((g) => g.id));
      setWorkspaceGroups(workspaceGroupsData.workspaceGroups);
    }
  }, [workspaceGroupsData]);

  const { data: workspaceCreditsData, refetch: refetchWorkspaceCredits } =
    useWorkspaceCreditsQuery();

  const { data: workspaceSubscriptionData } = useWorkspaceSubscriptionQuery();

  const {
    data: clientsData,
    loading,
    refetch: refetchClientStatistics,
  } = useWorkspaceClientStatisticsQuery({
    variables: {
      currentPage,
      pageSize: FETCH_LIMIT,
      sortField: currentSort.field || "created_at",
      sortDesc: currentSort.field ? !currentSort.asc : true,
      query,
      groupIds,
    },
  });

  useEffect(() => {
    if (
      clientsData?.workspaceClientStatistics?.totalMembers &&
      clientsData?.workspaceClientStatistics?.totalMembers !== totalMembers
    ) {
      setTotalMembers(clientsData.workspaceClientStatistics.totalMembers);
    }
  }, [clientsData?.workspaceClientStatistics?.totalMembers]);

  useEffect(() => {
    if (
      clientsData?.workspaceClientStatistics?.totalRecords &&
      clientsData?.workspaceClientStatistics?.totalRecords !== totalRecords
    ) {
      setTotalRecords(clientsData.workspaceClientStatistics.totalRecords);
    }
  }, [clientsData?.workspaceClientStatistics?.totalRecords]);

  // Query to fetch ALL users when selectAllUsers is true or when we have selections
  const {
    data: allUsersData,
    loading: allUsersLoading,
    refetch: refetchAllUsers,
  } = useWorkspaceClientStatisticsQuery({
    variables: {
      currentPage: 1,
      pageSize: 10000, // Large page size to get all users
      sortField: "created_at",
      sortDesc: true,
      query,
      groupIds,
    },
    skip: !needAllUsersData, // Use state variable to control when to fetch
  });

  const { currentWorkspaceCredits } = workspaceCreditsData || {
    currentWorkspaceCredits: "0",
  };

  const workspaceClientStatistics = clientsData?.workspaceClientStatistics || {
    data: [],
    totalRecords: 0,
  };

  const members = ensureNonNullArray(workspaceClientStatistics?.data);

  // Get all users when we need cross-page selection data - keep available when we have data
  const allUsers = allUsersData?.workspaceClientStatistics?.data
    ? ensureNonNullArray(allUsersData.workspaceClientStatistics.data)
    : [];

  const resetPageAndChecked = () => {
    setCurrentPage(1);
    setCheckedData([]);
    setSelectAllUsers(false);
    setExcludedUsers([]);
    setNeedAllUsersData(false);
  };

  const onSort = (fieldName: MemberTableField) => {
    resetPageAndChecked();
    updateSortState(fieldName);
  };

  // Add a separate state for the input value
  const [inputValue, setInputValue] = useState("");

  const onSearch = (q: string) => {
    resetPageAndChecked();
    setQuery(q);
  };

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400);

  // Trigger refetch of all users when selectAllUsers becomes true or when we have cross-page selections
  useEffect(() => {
    const shouldNeedAllUsersData = selectAllUsers || checkedData.length > 0;
    setNeedAllUsersData(shouldNeedAllUsersData);

    if (shouldNeedAllUsersData && !allUsersLoading) {
      refetchAllUsers();
    }
  }, [selectAllUsers, checkedData.length, refetchAllUsers, allUsersLoading]);

  const onCheckRow: OnCheckHandler = ({ member, checked, isCheckAll }) => {
    if (isCheckAll) {
      if (checked) {
        // Select only current page users (default behavior)
        const currentPageIds = members.map((m) => m.id);
        setCheckedData(currentPageIds);
        setSelectAllUsers(false);
        setExcludedUsers([]);
      } else {
        // Deselect all users
        setSelectAllUsers(false);
        setCheckedData([]);
        setExcludedUsers([]);
      }
    } else {
      // Individual row selection
      if (selectAllUsers) {
        if (!checked && member) {
          // When "select all" is active and we're unchecking a user,
          // add them to the excluded list
          setExcludedUsers((prev) => [...prev, member.id]);
        } else if (checked && member) {
          // When "select all" is active and we're checking a user,
          // remove them from the excluded list
          setExcludedUsers((prev) => prev.filter((id) => id !== member.id));
        }
      } else {
        // Normal individual selection logic
        if (!checked) {
          setCheckedData((prevIds) =>
            prevIds.filter((id) => id !== member?.id),
          );
        } else {
          if (member) {
            setCheckedData((prevIds) => [...prevIds, member?.id]);
          }
        }
      }
    }
  };

  const [dialogOpen, setDialogOpen] = useState<Dialogs>();
  const [updateUserRoleDto, setUpdateUserRoleDto] = useState<RoleDto>();
  const [selectedMember, setSelectedMember] = useState<StatsMember>();
  const [memberDetailDialogOpen, setMemberDetailDialogOpen] = useState(false);
  const [multiMode, setMultiMode] = useState(false);

  const [selectedInviteSchedule, setSelectedInviteSchedule] =
    useState<WorkspaceScheduleJob>();

  // For bulk operations, we need to handle "select all" differently
  // When selectAllUsers is true, we need to work with all users, not just current page
  const selectedMembers = useMemo(() => {
    if (!multiMode) {
      return selectedMember ? [selectedMember] : [];
    }

    if (selectAllUsers) {
      // When "select all" is active, use all users except excluded ones
      const result = allUsers.filter((m) => !excludedUsers.includes(m.id));
      return result;
    } else {
      // Normal explicit selection - check if we have cross-page selections
      const currentPageIds = members.map((m) => m.id);
      const hasSelectionsBeyondCurrentPage = checkedData.some(
        (id) => !currentPageIds.includes(id),
      );

      if (hasSelectionsBeyondCurrentPage && allUsers.length > 0) {
        // Use all users data to get selections from multiple pages
        const result = allUsers.filter((m) => checkedData.includes(m.id));
        return result;
      } else {
        // Use current page data only
        const result = members.filter((m) => checkedData.includes(m.id));
        return result;
      }
    }
  }, [
    multiMode,
    selectAllUsers,
    allUsers,
    excludedUsers,
    members,
    checkedData,
    selectedMember,
  ]);

  const tabs = getWorkspaceAdminTabs();

  const {
    data: invitationBatchesStatusData,
    startPolling: startPollingInvitationBatchesStatus,
    stopPolling: stopPollingInvitationBatchesStatus,
  } = useWorkspaceInvitaionBatchesStatusQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const _hasAnyRunningInvitationBatch =
        data?.workspaceInvitationBatchesStatus ===
        WorkspaceInvitationBatchesStatus.InProgress;

      if (!_hasAnyRunningInvitationBatch) {
        stopPollingInvitationBatchesStatus();
        // this is causing all kinds of bugs
        // refetchClientStatistics();
        refetchWorkspaceCredits();
      }
    },
    onError: () => {
      stopPollingInvitationBatchesStatus();
      // this is causing all kinds of bugs
      // refetchClientStatistics();
      refetchWorkspaceCredits();
    },
  });

  const {
    data: workspaceScheduleJobsData,
    refetch: refetchWorkspaceScheduleJobs,
    previousData: workspaceScheduleJobsPreviousData,
  } = useWorkspaceScheduleJobsQuery({
    variables: {
      jobTypes: [JobType.InviteClientsToWorkspace],
      statuses: [JobStatus.Scheduled],
    },
    pollInterval: 60000,
  });

  const invitationBatchesStatus =
    invitationBatchesStatusData?.workspaceInvitationBatchesStatus;

  const workspaceScheduleJobs = ensureNonNullArray(
    workspaceScheduleJobsData?.scheduledJobs,
  );

  const hasAnyRunningInvitationBatch =
    invitationBatchesStatus === WorkspaceInvitationBatchesStatus.InProgress;

  useEffect(() => {
    startPollingInvitationBatchesStatus(POLLING_INTERVAL_IN_MILLISECONDS);
    return () => {
      stopPollingInvitationBatchesStatus();
    };
  }, []);

  useEffect(() => {
    if (workspaceScheduleJobsPreviousData !== workspaceScheduleJobsData) {
      refetchClientStatistics();
      refetchWorkspaceCredits();
    }
  }, [workspaceScheduleJobsPreviousData, workspaceScheduleJobsData]);

  // Function to select all users across all pages
  const handleSelectAllPages = () => {
    setSelectAllUsers(true);
    setCheckedData([]);
    setExcludedUsers([]);
  };

  // Check if current page is fully selected (to show the banner)
  const isCurrentPageFullySelected =
    members.length > 0 &&
    !selectAllUsers &&
    members.every((member) => checkedData.includes(member.id));

  const hasSelectedUsers = selectAllUsers || checkedData.length > 0;

  return (
    <div className="ml-0 md:ml-sidebar">
      <WorkspaceAdminHeader initialTab="your-users" tabs={tabs} />

      <div className="flex flex-col gap-4 mx-auto mt-4 px-4 xl:px-0 max-w-6xl">
        <div className="flex flex-col md:flex-row gap-3">
          <WorkspaceSeats
            numMembers={totalMembers}
            subscription={
              workspaceSubscriptionData?.currentWorkspace?.subscription
            }
            totalPendingInvitations={
              workspaceSubscriptionData?.currentWorkspace
                ?.totalPendingInvitations
            }
          />

          <WorkspaceCredits initData={workspaceCreditsData} flex={1} />
        </div>

        {workspaceScheduleJobs.map((job) => (
          <div className="mt-3" key={job.id}>
            <BulkInviteSchedulingBanner
              onOpenBulkInviteDetail={setSelectedInviteSchedule}
              scheduleJob={job}
            />
          </div>
        ))}

        {hasAnyRunningInvitationBatch && (
          <div className="mt-3">
            <AddNewUsersCallout status={invitationBatchesStatus} />
          </div>
        )}

        <Card>
          <CardContent>
            <div
              className={cn(
                "w-full bg-white flex flex-col items-end md:flex-row gap-3 mb-3 justify-between",
                hasSelectedUsers && "flex-col-reverse",
              )}
            >
              <WorkspaceAdminController
                onAddUser={() => {
                  setDialogOpen("add-users");
                }}
                hasSelectedUsers={hasSelectedUsers}
                onSetGroup={() => {
                  setMultiMode(true);
                  setDialogOpen("set-group");
                }}
                onRemove={() => {
                  setMultiMode(true);
                  setDialogOpen("remove-users");
                }}
                onBulkSetCredits={() => {
                  setMultiMode(true);
                  setDialogOpen("modify-credits");
                }}
              />

              {/* Filtering and search */}
              <div className="flex items-center gap-2">
                <Filter
                  variant="multiple"
                  label={"Groups"}
                  options={workspaceGroups.map((option) => ({
                    value: option.id,
                    label: option.name,
                  }))}
                  values={selectedGroups}
                  size="lg"
                  onChange={setSelectedGroups}
                  rightIcon={
                    areGroupsLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Settings2 />
                    )
                  }
                  showDropdownIcon={false}
                />

                <SearchInput
                  value={inputValue}
                  loading={false}
                  onChange={(value) => {
                    setInputValue(value);
                    debouncedOnSearch(value);
                  }}
                  placeholder="Filter users..."
                />
              </div>
            </div>

            {/* Banner to select all pages when current page is selected */}
            {isCurrentPageFullySelected && (
              <div className="w-full my-3">
                <Callout variant="info" size="lg">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="font-bold">
                        {pluralize({
                          amount: checkedData.length,
                          singular: "user",
                          plural: "users",
                        })}
                      </div>
                      <span>&nbsp;selected on this page.</span>
                    </div>
                    <button
                      onClick={handleSelectAllPages}
                      className="text-blue-700 font-bold hover:text-blue-800 hover:underline cursor-pointer transition-colors flex items-center gap-1"
                    >
                      <CheckSquare2 className="h-4 w-4" /> Select all{" "}
                      {workspaceClientStatistics.totalRecords || 0} users across
                      all pages
                    </button>
                  </div>
                </Callout>
              </div>
            )}

            {/* Banner showing when all pages are selected */}
            {selectAllUsers && (
              <div className="w-full my-3">
                <Callout variant="info" size="lg" weight="bold">
                  <b>
                    {(workspaceClientStatistics.totalRecords || 0) -
                      excludedUsers.length}{" "}
                    user
                    {(workspaceClientStatistics.totalRecords || 0) -
                      excludedUsers.length !==
                    1
                      ? "s"
                      : ""}
                  </b>{" "}
                  selected across all pages
                  {excludedUsers.length > 0 && (
                    <span> ({excludedUsers.length} excluded)</span>
                  )}
                </Callout>
              </div>
            )}

            <div className=" bg-white mt-4">
              <WorkspaceMemberTable
                loading={loading}
                data={members}
                currentSort={currentSort}
                onSort={onSort}
                onTransferCredits={(row) => {
                  setSelectedMember(row);
                  setMultiMode(false);
                  setDialogOpen("transfer-credits");
                }}
                onUpdateRole={(row, newRole) => {
                  const { user, role: currentRole } = row;
                  const userName = user.name || user.email;
                  const isUpdatingCurrentUser = user.id === currentUser?.id;
                  setDialogOpen("update-user-role");
                  setUpdateUserRoleDto({
                    userName,
                    memberId: row.id,
                    currentRole,
                    newRole,
                    shouldReload: isUpdatingCurrentUser,
                  });
                }}
                onRemove={(row) => {
                  setSelectedMember(row);
                  setMultiMode(false);
                  setDialogOpen("remove-users");
                }}
                onSetGroup={(row) => {
                  setSelectedMember(row);
                  setMultiMode(false);
                  setDialogOpen("set-group");
                }}
                checkedData={checkedData}
                selectAllUsers={selectAllUsers}
                excludedUsers={excludedUsers}
                onCheck={onCheckRow}
                onSelectedMember={(row) => {
                  setSelectedMember(row);
                  setMemberDetailDialogOpen(true);
                }}
              />

              <ModifyCreditsDialog
                members={selectedMembers}
                selectAllUsers={selectAllUsers}
                excludedUsers={excludedUsers}
                totalRecords={workspaceClientStatistics.totalRecords || 0}
                workspaceCredit={currentWorkspaceCredits}
                open={dialogOpen === "modify-credits"}
                afterCreditSet={() => {
                  const hadCrossPageData =
                    selectAllUsers || checkedData.length > 0;
                  setCheckedData([]);
                  setSelectAllUsers(false);
                  setExcludedUsers([]);
                  refetchWorkspaceCredits();
                  refetchClientStatistics();
                  if (hadCrossPageData && allUsers.length > 0) {
                    refetchAllUsers();
                  }
                  setMemberDetailDialogOpen(false);
                }}
                onClose={() => {
                  setDialogOpen(undefined);
                }}
              />

              {selectedMember && (
                <TransferCreditsDialog
                  source={LedgerAccountKeyUtils.WorkspaceAvailable()}
                  target={LedgerAccountKeyUtils.WorkspaceMemberAvailable(
                    selectedMember.id,
                  )}
                  open={dialogOpen === "transfer-credits"}
                  onComplete={(ok) => {
                    if (ok) {
                      setSelectedMember(undefined);
                      setDialogOpen(undefined);
                    }
                    refetchWorkspaceCredits();
                    refetchClientStatistics();
                  }}
                  onClose={() => {
                    setDialogOpen(undefined);
                  }}
                />
              )}
              <AddUsersDialog
                workspaceCredit={currentWorkspaceCredits}
                open={dialogOpen === "add-users"}
                afterAddedMember={() => {
                  resetPageAndChecked();
                  refetchWorkspaceCredits();
                  startPollingInvitationBatchesStatus(
                    POLLING_INTERVAL_IN_MILLISECONDS,
                  );
                  refetchWorkspaceScheduleJobs();
                }}
                onClose={() => {
                  setDialogOpen(undefined);
                }}
              />

              {selectedInviteSchedule && (
                <BulkInviteScheduleDetailDialog
                  inviteSchedule={selectedInviteSchedule}
                  workspaceCredit={currentWorkspaceCredits}
                  afterAddedMember={() => {
                    resetPageAndChecked();
                    refetchWorkspaceCredits();
                    startPollingInvitationBatchesStatus(
                      POLLING_INTERVAL_IN_MILLISECONDS,
                    );
                    refetchWorkspaceScheduleJobs();
                  }}
                  onClose={() => {
                    setSelectedInviteSchedule(undefined);
                  }}
                  afterModifyInvite={() => {
                    refetchWorkspaceScheduleJobs();
                  }}
                />
              )}

              <UpdateUserRoleDialog
                open={dialogOpen === "update-user-role"}
                dto={updateUserRoleDto}
                onClose={() => {
                  setDialogOpen(undefined);
                }}
              />

              <RemoveUsersDialog
                members={selectedMembers}
                selectAllUsers={selectAllUsers}
                excludedUsers={excludedUsers}
                totalRecords={workspaceClientStatistics.totalRecords || 0}
                open={dialogOpen === "remove-users"}
                afterDeletedMember={() => {
                  const hadCrossPageData =
                    selectAllUsers || checkedData.length > 0;
                  setCheckedData([]);
                  setSelectAllUsers(false);
                  setExcludedUsers([]);
                  refetchWorkspaceCredits();
                  refetchClientStatistics();
                  if (hadCrossPageData && allUsers.length > 0) {
                    refetchAllUsers();
                  }
                  setMemberDetailDialogOpen(false);
                }}
                onClose={() => {
                  setDialogOpen(undefined);
                }}
              />

              {selectedMember && (
                <MemberDetailsDialog
                  member={selectedMember}
                  open={memberDetailDialogOpen}
                  onClose={() => {
                    setMemberDetailDialogOpen(false);
                  }}
                  onRemove={(row) => {
                    setSelectedMember(row);
                    setMultiMode(false);
                    setDialogOpen("remove-users");
                  }}
                  onTransferCredits={(row) => {
                    setSelectedMember(row);
                    setDialogOpen("transfer-credits");
                  }}
                  onSetGroup={(row) => {
                    setSelectedMember(row);
                    setMultiMode(false);
                    setDialogOpen("set-group");
                  }}
                />
              )}

              <SetGroupForUsersDialog
                members={selectedMembers}
                selectAllUsers={selectAllUsers}
                excludedUsers={excludedUsers}
                totalRecords={workspaceClientStatistics.totalRecords || 0}
                open={dialogOpen === "set-group"}
                afterSetGroup={() => {
                  const hadCrossPageData =
                    selectAllUsers || checkedData.length > 0;
                  setCheckedData([]);
                  setSelectAllUsers(false);
                  setExcludedUsers([]);
                  refetchClientStatistics();
                  if (hadCrossPageData && allUsers.length > 0) {
                    refetchAllUsers();
                  }
                  setMemberDetailDialogOpen(false);
                }}
                onClose={() => {
                  setDialogOpen(undefined);
                }}
              />
            </div>

            <Paging
              style={{ background: "white", padding: 10 }}
              currentPage={currentPage}
              pageSize={FETCH_LIMIT}
              setCurrentPage={(index) => {
                setCurrentPage(index);
              }}
              totalRecords={totalRecords}
              isSticky={true}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function WorkspaceAdminPage() {
  return (
    <Authenticate
      requireWorkspaceAgent={true}
      maxWidth="100%"
      layout="dashboard"
    >
      <Nav />
      <WorkspaceAdminContainer />
    </Authenticate>
  );
}
